import React, { useContext } from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { Link as RouterLink } from "react-router-dom";
import { dayTimeIn } from "../../utils/restaurantClock";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import ConfigurationContext from "../../context/Configuration";
import { resizedImage } from "../../utils/resizedImage";
import { C, FOCUS_RING, FONT_BODY, FONT_DISPLAY, RADIUS, VISUALLY_HIDDEN } from "../../utils/designTokens";
import { orderTotal, titleCaseIfShouting } from "./orderText";
import { isInStoreOrder } from "./OrderDetail/orderProgress";

// The status chip's words and the tracker step, delivery and pickup.
const DELIVERY = {
  SCHEDULED: { label: "Scheduled", step: 0, headline: "Your order is scheduled" },
  PENDING: { label: "Order placed", step: 0, headline: "Waiting for the restaurant to accept" },
  ACCEPTED: { label: "Accepted", step: 1, headline: "Your order is being prepared" },
  PREPARED: { label: "Ready", step: 2, headline: "Your food is ready" },
  ASSIGNED: { label: "Driver assigned", step: 2, headline: "A driver has been assigned" },
  EN_ROUTE_PICKUP: { label: "Driver en route", step: 2, headline: "Your driver is heading to the restaurant" },
  ARRIVED_PICKUP: { label: "At the restaurant", step: 2, headline: "Your driver is at the restaurant" },
  PICKED: { label: "Picked up", step: 3, headline: "Your order is on the way" },
  EN_ROUTE_DROPOFF: { label: "On the way", step: 3, headline: "Your order is on the way" },
  ARRIVED_DROPOFF: { label: "At your door", step: 3, headline: "Your driver is at your door" },
  DELIVERED: { label: "Delivered", step: 4, headline: "Your order was delivered" },
};
const PICKUP = {
  SCHEDULED: { label: "Scheduled", step: 0, headline: "Your pickup is scheduled" },
  PENDING: { label: "Order placed", step: 0, headline: "Waiting for the restaurant to accept" },
  ACCEPTED: { label: "Accepted", step: 1, headline: "Your order is being prepared" },
  PREPARED: { label: "Ready for pickup", step: 2, headline: "Ready for pickup" },
  ASSIGNED: { label: "Ready for pickup", step: 2, headline: "Ready for pickup" },
  EN_ROUTE_PICKUP: { label: "Ready for pickup", step: 2, headline: "Ready for pickup" },
  ARRIVED_PICKUP: { label: "Ready for pickup", step: 2, headline: "Ready for pickup" },
  PICKED: { label: "Picked up", step: 3, headline: "You picked up your order" },
  EN_ROUTE_DROPOFF: { label: "Picked up", step: 3, headline: "You picked up your order" },
  ARRIVED_DROPOFF: { label: "Picked up", step: 3, headline: "You picked up your order" },
  DELIVERED: { label: "Picked up", step: 3, headline: "You picked up your order" },
};
const STEPS_DELIVERY = [
  { label: "Placed", icon: ShoppingBagRoundedIcon },
  { label: "Accepted", icon: CheckRoundedIcon },
  { label: "Ready", icon: RoomServiceRoundedIcon },
  { label: "On the way", icon: LocalShippingRoundedIcon },
  { label: "Delivered", icon: HomeRoundedIcon },
];
const STEPS_PICKUP = [
  { label: "Placed", icon: ShoppingBagRoundedIcon },
  { label: "Accepted", icon: CheckRoundedIcon },
  { label: "Ready", icon: RoomServiceRoundedIcon },
  { label: "Picked up", icon: StorefrontRoundedIcon },
];
// A driver can be assigned before the food is marked prepared; until then
// the order hasn't reached Ready.
const PRE_READY = ["ASSIGNED", "EN_ROUTE_PICKUP", "ARRIVED_PICKUP"];

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255,255,255,0.75); }
  70% { box-shadow: 0 0 0 8px rgba(255,255,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
`;

/** Who brings it: "Pickup", "Restaurant delivery", "RushMyFood delivery". */
export function deliveryKind(o) {
  if (isInStoreOrder(o)) return "In-store order";
  if (o.isPickedUp) return "Pickup";
  if (o.rider?.driverType === "RESTAURANT") return "Restaurant delivery";
  if (o.rider?.driverType === "RUSHMYFOOD") return "RushMyFood delivery";
  if (o.firstDeliveryOrderId) return "RushMyFood delivery";
  if (o.isSelfDelivery) return "Restaurant delivery";
  return "RushMyFood delivery";
}

function Tracker({ steps, current }) {
  return (
    <Box component="ol" aria-label="Order progress" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))` }}>
      {steps.map((s, i) => {
        const done = i <= current;
        const isCurrent = i === current;
        const Icon = s.icon;
        return (
          <Box
            component="li"
            key={s.label}
            aria-current={isCurrent ? "step" : undefined}
            sx={{ position: "relative", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", minWidth: 0 }}
          >
            {/* The line into this step, between the circles only. */}
            {i > 0 && (
              <Box
                aria-hidden="true"
                sx={{ position: "absolute", top: 20, left: "calc(-50% + 26px)", width: "calc(100% - 52px)", height: 4, borderRadius: 2, bgcolor: i <= current ? C.red : C.border, zIndex: 0 }}
              />
            )}
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: done ? C.red : C.surface2,
                color: done ? "#FFFFFF" : "#9A968F",
                boxShadow: isCurrent ? `0 0 0 6px ${C.redTint}` : "none",
              }}
            >
              <Icon aria-hidden="true" sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ mt: 1, px: 0.5, fontFamily: FONT_BODY, fontSize: { xs: 11, sm: 13 }, lineHeight: 1.25, fontWeight: isCurrent ? 800 : 600, color: done ? C.ink : "#9A968F" }}>
              {s.label}
              {isCurrent && <Box component="span" sx={VISUALLY_HIDDEN}> (now)</Box>}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

const ghostLink = {
  ...FOCUS_RING,
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 0.75,
  height: 44,
  px: 2,
  borderRadius: "14px",
  border: "1.5px solid rgba(255,255,255,0.7)",
  color: "#FFFFFF",
  fontFamily: FONT_BODY,
  fontSize: 14,
  fontWeight: 700,
  textDecoration: "none",
  whiteSpace: "nowrap",
  "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
  "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "2px" },
};

/** One order in progress: status, what and where, tracking links, and the tracker. */
function LiveOrderBanner({ order }) {
  const configuration = useContext(ConfigurationContext);
  const currency = configuration?.currencySymbol || "$";
  const pickup = Boolean(order.isPickedUp) || isInStoreOrder(order);
  const table = pickup ? PICKUP : DELIVERY;
  const config = table[order.orderStatus] || table.PENDING;
  let step = config.step;
  if (PRE_READY.includes(order.orderStatus) && !order.preparedAt) step = 1;
  const steps = pickup ? STEPS_PICKUP : STEPS_DELIVERY;
  const items = order.items || [];
  const count = items.reduce((n, it) => n + (parseInt(it.quantity, 10) || 0), 0);
  const photo = order.restaurant?.image || order.restaurant?.logo;
  const itemLine = items.slice(0, 3).map((it) => `${it.quantity}× ${titleCaseIfShouting(it.title)}`).join(", ") + (items.length > 3 ? `, +${items.length - 3} more` : "");

  return (
    <Box
      component="section"
      aria-label={`${config.headline}, ${order.restaurant?.name || "your order"}`}
      sx={{ position: "relative", overflow: "hidden", borderRadius: "26px", bgcolor: C.red, color: "#FFFFFF", p: { xs: "22px 18px 18px", sm: "28px 28px 24px" } }}
    >
      <Box aria-hidden="true" sx={{ position: "absolute", width: 260, height: 260, borderRadius: "50%", left: -60, bottom: -150, bgcolor: "rgba(255,255,255,0.08)" }} />
      <Box aria-hidden="true" sx={{ position: "absolute", width: 220, height: 220, borderRadius: "50%", right: -50, top: -110, bgcolor: "rgba(255,255,255,0.06)" }} />

      <Box sx={{ position: "relative", display: "flex", flexWrap: "wrap", alignItems: "center", gap: { xs: 2, sm: 2.5 } }}>
        <Box sx={{ width: 72, height: 72, flexShrink: 0, borderRadius: "50%", border: "3px solid #FFFFFF", overflow: "hidden", bgcolor: C.bg, boxSizing: "border-box" }}>
          {photo && <Box component="img" src={resizedImage(photo, 240)} alt={order.restaurant?.name || ""} sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
        </Box>
        <Box sx={{ flex: "1 1 320px", minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
              <Box component="span" aria-hidden="true" sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#FFFFFF", animation: `${pulse} 1.8s ease-out infinite`, "@media (prefers-reduced-motion: reduce)": { animation: "none" } }} />
              Live · {config.label}
            </Box>
            <Box component="span" sx={{ height: 22, px: 1, display: "inline-flex", alignItems: "center", borderRadius: RADIUS.pill, bgcolor: "rgba(255,255,255,0.18)", fontFamily: FONT_BODY, fontSize: 12, fontWeight: 700 }}>
              {deliveryKind(order)}
            </Box>
          </Box>
          <Box component="h2" sx={{ m: 0, mt: 0.5, fontFamily: FONT_DISPLAY, fontSize: { xs: 24, sm: 30 }, fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.02em" }}>
            {config.headline}
          </Box>
          <Box component="p" sx={{ m: 0, mt: 0.75, fontFamily: FONT_BODY, fontSize: 14, color: "#FBD9D5" }}>
            {[order.restaurant?.name, order.orderId ? `#${order.orderId}` : null, `${count} ${count === 1 ? "item" : "items"}`, `${currency}${orderTotal(order).toFixed(2)}`].filter(Boolean).join(" · ")}
          </Box>
          {itemLine && (
            <Box component="p" sx={{ m: 0, mt: 0.25, fontFamily: FONT_BODY, fontSize: 13, color: "rgba(255,255,255,0.8)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {itemLine}
            </Box>
          )}
          {order.orderStatus === "SCHEDULED" && order.orderDate && (
            <Box component="p" sx={{ m: 0, mt: 0.75, display: "inline-flex", alignItems: "center", gap: 0.75, fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700 }}>
              <EventRoundedIcon aria-hidden="true" sx={{ fontSize: 17 }} />
              {pickup ? "Pickup" : "Delivery"} scheduled for {dayTimeIn(order.orderDate, order.restaurant?.timezone)}
            </Box>
          )}
        </Box>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, justifyContent: { xs: "flex-start", md: "flex-end" }, flex: { xs: "1 1 100%", md: "0 0 auto" } }}>
          <Box
            component={RouterLink}
            to={`/order-detail/${order._id}`}
            sx={{
              ...FOCUS_RING,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              height: 52,
              px: 2.5,
              borderRadius: "16px",
              bgcolor: "#FFFFFF",
              color: "#8E1C14",
              fontFamily: FONT_BODY,
              fontSize: 15,
              fontWeight: 800,
              textDecoration: "none",
              whiteSpace: "nowrap",
              "&:hover": { bgcolor: "#FFF5F4" },
              "&:focus-visible": { outline: "2px solid #FFFFFF", outlineOffset: "3px" },
            }}
          >
            Track order →
          </Box>
          {/* The courier's own live pages, when it gives them. */}
          {order.fdTrackerLink && (
            <Box component="a" href={order.fdTrackerLink} target="_blank" rel="noreferrer" sx={{ ...ghostLink, height: 52 }}>
              Track live
              <OpenInNewRoundedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
              <Box component="span" sx={VISUALLY_HIDDEN}>(opens in a new tab)</Box>
            </Box>
          )}
          {order.fdDspTrackerLink && (
            <Box component="a" href={order.fdDspTrackerLink} target="_blank" rel="noreferrer" sx={{ ...ghostLink, height: 52 }}>
              Track driver
              <OpenInNewRoundedIcon aria-hidden="true" sx={{ fontSize: 16 }} />
              <Box component="span" sx={VISUALLY_HIDDEN}>(opens in a new tab)</Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box sx={{ position: "relative", mt: 2.5, bgcolor: C.bg, color: C.ink, borderRadius: "20px", p: { xs: "18px 8px 14px", sm: "20px 16px 16px" } }}>
        <Tracker steps={steps} current={step} />
      </Box>
    </Box>
  );
}

export default React.memo(LiveOrderBanner);
